import React, { useMemo, useState } from 'react';
import { GeneratedMealPlan, GroceryCategory } from '../../utils/MealPlanner/types';

type ShoppingListProps = {
  plan: GeneratedMealPlan | null;
};

const categoryLabels: Record<GroceryCategory, string> = {
  produce: 'Produce',
  protein: 'Protein',
  dairy: 'Dairy',
  bakery: 'Bakery',
  pantry: 'Pantry',
  frozen: 'Frozen',
  other: 'Other',
};

const categoryOrder: GroceryCategory[] = ['produce', 'protein', 'dairy', 'bakery', 'pantry', 'frozen', 'other'];

function ShoppingList({ plan }: ShoppingListProps) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const grouped = useMemo(() => {
    const groups = new Map<GroceryCategory, NonNullable<typeof plan>['shoppingList']>();

    plan?.shoppingList.forEach((item) => {
      const current = groups.get(item.category) ?? [];
      groups.set(item.category, [...current, item]);
    });

    return categoryOrder
      .map((category) => ({ category, items: groups.get(category) ?? [] }))
      .filter((group) => group.items.length > 0);
  }, [plan]);

  if (!plan) {
    return (
      <section className="meal-planner-panel meal-planner-empty">
        <h2>Shopping list</h2>
        <p>Your optimized aisle list will appear here.</p>
      </section>
    );
  }

  return (
    <section className="meal-planner-panel meal-planner-shopping" aria-labelledby="shopping-list-title">
      <div className="meal-planner-panel-header">
        <div>
          <h2 id="shopping-list-title">Shopping list</h2>
          <p>{plan.shoppingList.length} grouped items</p>
        </div>
        <strong>EUR {plan.totalCost.toFixed(2)}</strong>
      </div>

      {grouped.map((group) => (
        <div key={group.category} className="meal-planner-shopping-group">
          <h3>{categoryLabels[group.category]}</h3>
          <ul>
            {group.items.map((item) => {
              const itemKey = `${item.ingredientId}-${item.productName}`;

              return (
                <li key={itemKey} className={checked[itemKey] ? 'is-checked' : ''}>
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(checked[itemKey])}
                      onChange={(event) => setChecked({ ...checked, [itemKey]: event.target.checked })}
                    />
                    <span>
                      <strong>{item.name}</strong>
                      <small>
                        {item.packages} x {item.productName}
                        {item.missingPrice ? ' · price missing' : ''}
                      </small>
                    </span>
                  </label>
                  <b>EUR {item.totalPrice.toFixed(2)}</b>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

export default ShoppingList;
